import React, { useEffect, useCallback, useState, useRef } from "react";
import { Button, Container, Form, Image, Spinner } from "react-bootstrap";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FaRegCalendar } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { Popper } from "@mui/material";
import dayjs from "dayjs";

import "bootstrap/dist/css/bootstrap.min.css";
import "./basic-details.css";

import logo2 from "../../../assets/images/logo2.png";
import backarrow from "../../../assets/images/backarrow.jpg";
import responsivebg from "../../../assets/images/responsive-bg.png";

import Stepper from "../Stepper";
import log from "../../../utils/log";
import * as userActions from "../../../actions/user";
import * as userPreferencesActions from "../../../actions/userPreferences";
import { useCookies } from "../../../hooks/useCookies";
import { useAppContext } from "../../../Context/UseContext";


export const BasicDetails = () => {
  const navigate = useNavigate();
  const isFetched = useRef(false);

  const { getCookie } = useCookies();
  const token = getCookie("token");
  const { userDetails, setUserDetails } = useAppContext();
  const [errorMessage, setErrorMessage] = useState("");
  const [showInfo, setShowInfo] = useState(false);
  const [age, setAge] = useState(null);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const [adulthood, setAdulthood] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null); // Initialize with null
  const [errorBrithday, setErrorBrithday] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClick = event => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const fetchUserDetails = useCallback(async () => {
    try {
      const data = await userActions.getNameDetails();
      setUserDetails(data);

      if (data.birthday) {
        setSelectedDate(dayjs(data.birthday));
        calculateAge(data.birthday);
      }
    } catch (error) {
      log.error(error);
    }
  }, [setUserDetails]);

  useEffect(() => {
    if (isFetched.current) return;
    isFetched.current = true;

    if (!token) return;
    fetchUserDetails();
  }, [token, fetchUserDetails]);

  const handleNameChange = e => {
    setErrorMessage("");
    const value = e.target.value.replace(/[^a-zA-Z]/g, "");
    setUserDetails(prevDetails => ({
      ...prevDetails,
      first_name: value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
    }));
  };

  const handleLastNameChange = e => {
    const value = e.target.value.replace(/[^a-zA-Z]/g, "");
    setUserDetails(prevDetails => ({
      ...prevDetails,
      last_name: value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
    }));
  };

  const handleBirthdayChange = value => {
    const date = dayjs(value);
    if (date.isValid()) {
      setSelectedDate(date); // Update the selected date state
      setUserDetails(prevDetails => ({
        ...prevDetails,
        birthday: date.format("YYYY-MM-DD"),
      }));
      calculateAge(date);
    }
  };

  const calculateAge = birthday => {
    setErrorBrithday("");
    setAdulthood(true);
    const birthDate = new Date(birthday);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    setAge(age);
  };

  const updatePreferences = async calculatedAge => {
    try {
      let age_from = calculatedAge - 5;
      let age_to = calculatedAge + 5;

      // Ensure age_from is at least 18 if the user's age is less than 23
      if (calculatedAge < 23) {
        age_from = Math.max(age_from, 18);
      }

      await userPreferencesActions.updateAge(
        JSON.stringify({
          age_from,
          age_to,
        })
      );
    } catch (error) {
      log.error(error);
      setErrorMessage("An error occurred while updating your preferences.");
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    // Reset error messages
    setErrorMessage("");
    setErrorBrithday("");


    if (!userDetails.first_name) {
      setErrorMessage("Name is required");
      return;
    }
    if (userDetails.first_name?.length < 2) {
      setErrorMessage("*Name must be at least two letters long.");
      return;
    }

    if (
      userDetails.first_name.includes("@") ||
      userDetails.first_name.includes("#") ||
      userDetails.first_name.includes("$") ||
      userDetails.first_name.includes("%") ||
      userDetails.first_name.includes("^") ||
      userDetails.first_name.includes("&") ||
      userDetails.first_name.includes("*") ||
      userDetails.first_name.includes("(") ||
      userDetails.first_name.includes(")") ||
      userDetails.first_name.includes("-") ||
      userDetails.first_name.includes("+") ||
      userDetails.first_name.includes("=") ||
      userDetails.first_name.includes("[") ||
      userDetails.first_name.includes("]") ||
      userDetails.first_name.includes("{") ||
      userDetails.first_name.includes("}") ||
      userDetails.first_name.includes("|") ||
      userDetails.first_name.includes("\\") ||
      userDetails.first_name.includes(";") ||
      userDetails.first_name.includes(":") ||
      userDetails.first_name.includes("'") ||
      userDetails.first_name.includes('"') ||
      userDetails.first_name.includes("<") ||
      userDetails.first_name.includes(">") ||
      userDetails.first_name.includes(",") ||
      userDetails.first_name.includes(".") ||
      userDetails.first_name.includes("/") ||
      userDetails.first_name.includes("?") ||
      userDetails.first_name.includes("!") ||
      userDetails.first_name.includes("`") ||
      userDetails.first_name.includes("~")
    ) {
      setErrorMessage("*Please enter a valid name");
      return;
    }

    if (!userDetails.birthday) {
      setErrorBrithday("*Please select your birth date.");
      return;
    }

    const calculatedAge = dayjs().diff(dayjs(userDetails.birthday), "years");
    if (calculatedAge <= 18) {
      setAdulthood(false);
      return;
    }

    setLoading(true);
    try {
      await updatePreferences(calculatedAge);
      await userActions.updateNameDetails(JSON.stringify(userDetails));
      setErrorMessage("");
      navigate("/abtyourself");
    } catch (error) {
      log.error(error);
      setErrorMessage("An error occurred while updating your profile.");
    } finally {
      setLoading(false);
    }
  };

  const toggleInfoVisibility = () => {
    setShowInfo(!showInfo);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="basicdetails-container">
        {open && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "transparent",
              zIndex: 10,
            }}
            onClick={() => setAnchorEl(null)}
          ></div>
        )}
        <div className="basic-details-bg">
          <Image className="banner-image" src={responsivebg}></Image>
        </div>
        <Container className="basicdetails-main">
          <Container className="basicdetails-content">
            <Container className="logo-progressbar4">
              <Container className="logo-arrow4">
                <Image
                  src={backarrow}
                  className="backarrow"
                  onClick={() => window.history.back()}
                />
                <Image
                  src={logo2}
                  alt="Logo"
                  className="logo"
                  style={{ backgroundColor: "transparent" }}
                />
              </Container>
              <Stepper activeStep="BasicDetails" />
            </Container>
            <Container className="basic-details-text">
              <p>Tell us about yourself</p>
            </Container>
            <Container className="basic-details-details">
              <Form onSubmit={handleSubmit} className="basic-details-form">
                <Form.Group
                  controlId="formName"
                  className="basic-details-group"
                >
                  <Form.Label className="basic-details-label">
                    What's your name?{" "}
                    <span className="required-asterisk text-danger">*</span>
                  </Form.Label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Form.Control
                      className={`basic-details-input custom-input ${errorMessage ? "error" : ""
                        }`}
                      type="text"
                      placeholder="First name"
                      value={userDetails.first_name || ""}
                      onChange={handleNameChange}
                      style={{ flex: 1 }}
                    />
                  </div>
                  <div style={{ marginTop: "5px" }}>
                    {errorMessage && (
                      <Form.Text className="text-danger error-message">
                        {errorMessage}
                      </Form.Text>
                    )}
                  </div>
                </Form.Group>

                <Form.Group
                  controlId="formLastName"
                  className="basic-details-group purplebox"
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Form.Control
                      className="basic-details-input-verify custom-input"
                      type="text"
                      placeholder="Last name (Optional)"
                      value={userDetails.last_name || ""}
                      onChange={handleLastNameChange}
                      style={{
                        flex: 1,
                        marginTop: "12px",
                        marginBottom: "24px",
                      }}
                    />
                    <div className="last-name-why-div">
                      <Button
                        className="last-name-why"
                        onClick={toggleInfoVisibility}
                      >
                        ?
                      </Button>
                    </div>
                  </div>
                  <Container
                    className={`why-info ${showInfo ? "" : "why-info-hidden"}`}
                  >
                    <span>Why last name?</span>
                    <p>
                      It helps us to build a trusted and authentic community for
                      you and others. Only your first name is shown publicly.
                    </p>
                  </Container>
                </Form.Group>
                <Form.Group
                  controlId="formBirthday"
                  className="basic-details-group"
                >
                  <Form.Label className="basic-details-label">
                    When is your birthday?{" "}
                    <span className="required-asterisk text-danger">*</span>
                  </Form.Label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        cursor: "pointer",
                        border: "1px solid black",
                        borderRadius: "10px",
                        overflow: "hidden",
                      }}
                      onClick={handleClick}
                    >
                      <Form.Control
                        className={`basic-details-input-verify custom-input ${errorBrithday ? "error" : ""
                          }`}
                        type="text"
                        placeholder="Year / Month / Day"
                        value={userDetails.birthday || ""}
                        style={{
                          flex: 1,
                          border: errorBrithday ? 1 : 0,
                          borderColor: errorBrithday ? "red" : "transparent",
                          cursor: "pointer",
                        }}
                        readOnly
                      />
                      <FaRegCalendar
                        style={{
                          marginRight: "10px",
                        }}
                      />
                    </div>
                    <Popper
                      sx={{
                        backgroundColor: "white",
                        zIndex: 9999,
                        borderRadius: "10px",
                        border: "1px solid black",
                      }}
                      open={open}
                      anchorEl={anchorEl}
                      placement="bottom-start"
                    >
                      <DateCalendar
                        value={selectedDate} // Set the initial selected date
                        onChange={value => {
                          handleBirthdayChange(
                            dayjs(value).format("YYYY-MM-DD")
                          );
                        }}
                        disableFuture
                      />
                    </Popper>
                  </div>
                  <div style={{ marginTop: "5px" }}>
                    {errorBrithday && (
                      <Form.Text className="text-danger error-message">
                        {errorBrithday}
                      </Form.Text>
                    )}

                    {age !== null && age !== 54 && (
                      <span className="calculated-age">
                        Your age will be displayed as {age}
                      </span>
                    )}
                    {!adulthood && userDetails.birthday && (
                      <p className="text-danger error-message">
                        You must be over 19 to join MTD
                      </p>
                    )}
                  </div>
                </Form.Group>

                <div
                  className="d-flex justify-content-center"
                  style={{
                    position: "fixed",
                    bottom: "30px",
                    width: "100%",
                    gap: "30px",
                  }}
                >
                  <button
                    type="submit"
                    className="global-next-bottom-fix-btn"
                    disabled={loading}
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" role="status" />
                    ) : (
                      "Next"
                    )}
                  </button>
                </div>
              </Form>
            </Container>
          </Container>
        </Container>
      </div>
    </LocalizationProvider>
  );
};

export default BasicDetails;
